import React, { useState, useEffect } from 'react';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import AuthModal from './pages/AuthModal';
import Dashboard from './pages/Dashboard';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/HelpPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

function App() {
    // View State: 'landing' | 'dashboard' | 'settings' | 'help' | 'privacy'
    const [currentPage, setCurrentPage] = useState('landing');
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [authMode, setAuthMode] = useState('signup');
    
    // User Profile (from localStorage)
    const [userProfile, setUserProfile] = useState(null);
    
    // Trip State
    const [tripPlan, setTripPlan] = useState(null);

    // User Preferences (from localStorage)
    const [darkMode, setDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('spotlogs_theme');
        return savedTheme === 'dark';
    });

    // Load profile from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem('spotlogs_profile');
        if (saved) {
            const profile = JSON.parse(saved);
            setUserProfile(profile);
            if (currentPage === 'landing') {
                setCurrentPage('dashboard');
            }
        }
    }, [currentPage]);

    // Apply dark mode class and save to localStorage
    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
            localStorage.setItem('spotlogs_theme', 'dark');
        } else {
            document.body.classList.remove('dark-mode');
            localStorage.setItem('spotlogs_theme', 'light');
        }
    }, [darkMode]);

    // Handlers
    const handleShowAuth = (mode = 'signup') => {
        setAuthMode(mode);
        setShowOnboarding(true);
    };

    const handleStartFromLanding = (mode = 'signup') => {
        if (userProfile) {
            setCurrentPage('dashboard');
        } else {
            handleShowAuth(mode);
        }
    };

    const handleOnboardingComplete = (profile) => {
        setUserProfile(profile);
        setShowOnboarding(false);
        setCurrentPage('dashboard');
    };

    const handleNavigate = (page) => {
        setCurrentPage(page);
    };

    const handleSignOut = () => {
        localStorage.removeItem('spotlogs_profile');
        setUserProfile(null);
        setTripPlan(null);
        setCurrentPage('landing');
    };

    // Global Modal
    const renderAuthModal = () => (
        <AuthModal
            show={showOnboarding}
            initialMode={authMode}
            onComplete={handleOnboardingComplete}
            onClose={() => setShowOnboarding(false)}
        />
    );

    // Render based on current page
    if (currentPage === 'landing') {
        return (
            <>
                <LandingPage onStart={handleStartFromLanding} onNavigate={handleNavigate} />
                {renderAuthModal()}
            </>
        );
    }

    if (currentPage === 'settings') {
        return (
            <>
                <SettingsPage
                    currentPage={currentPage}
                    onNavigate={handleNavigate}
                    onSignOut={handleSignOut}
                    userProfile={userProfile}
                    setUserProfile={setUserProfile}
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                    onShowAuth={handleShowAuth}
                />
                {renderAuthModal()}
            </>
        );
    }

    if (currentPage === 'help') {
        return (
            <>
                <HelpPage
                    currentPage={currentPage}
                    onNavigate={handleNavigate}
                    onSignOut={handleSignOut}
                    userProfile={userProfile}
                    onShowAuth={handleShowAuth}
                />
                {renderAuthModal()}
            </>
        );
    }

    if (currentPage === 'privacy') {
        return (
            <>
                <PrivacyPolicyPage
                    currentPage={currentPage}
                    onNavigate={handleNavigate}
                    onSignOut={handleSignOut}
                    userProfile={userProfile}
                    onShowAuth={handleShowAuth}
                />
                {renderAuthModal()}
            </>
        );
    }

    // Default: Dashboard
    return (
        <>
            <Dashboard
                currentPage={currentPage}
                onNavigate={handleNavigate}
                onSignOut={handleSignOut}
                userProfile={userProfile}
                tripPlan={tripPlan}
                setTripPlan={setTripPlan}
                darkMode={darkMode}
            />
            {renderAuthModal()}
        </>
    );
}

export default App;